import { HTMLAttributes } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Grid, CircularProgress } from '@material-ui/core'
import { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone'
import { config, MAX_SIZE_POWER_OF_ATTORNEY_MB } from 'config'
import { MODULE_NAME } from 'modules/Lawsuits/strings'
import { useExtensionsString, SIZE_UNIT } from './FileUploader.utils'
import {
  DropTitle,
  DropWrapper,
  HighlightedText,
  UploadIcon,
  DropHint,
  DropHintBold,
  LOADER_SIZE,
  LOADER_THICKNESS,
  MaxFilesTitle,
} from './FileUploader.style'

const ID_PREFIX = 'file-uploader'

interface FileUploaderProps extends Partial<HTMLAttributes<HTMLDivElement>> {
  loading?: boolean
  inputProps: DropzoneInputProps
  rootProps: DropzoneRootProps
  allowedExtensions?: string[]
  maxSize?: number
  sizeUnit?: string
  showHint?: boolean
  maxFilesNumber?: number
  files?: File[]
}

const FileUploader = ({
  id,
  loading,
  inputProps,
  rootProps,
  showHint = true,
  files = [],
  maxFilesNumber = 1,
  sizeUnit = SIZE_UNIT,
  maxSize = MAX_SIZE_POWER_OF_ATTORNEY_MB,
  allowedExtensions = config.ALLOWED_POWER_OF_ATTORNEY_EXTENSIONS,
  ...props
}: FileUploaderProps) => {
  const { t } = useTranslation(MODULE_NAME)
  const translationExtensions = useExtensionsString(allowedExtensions)

  const isFileLimitReached = !!maxFilesNumber && maxFilesNumber <= files?.length

  return (
    <DropWrapper
      container
      justifyContent="center"
      alignItems="center"
      direction="column"
      disabled={isFileLimitReached}
      loading={loading}
      {...rootProps}
      {...props}
    >
      <input
        {...inputProps}
        disabled={inputProps.disabled || isFileLimitReached}
        id={id ? `${id}-file-input` : `${ID_PREFIX}-file-input`}
      />
      {loading ? (
        <CircularProgress size={LOADER_SIZE} thickness={LOADER_THICKNESS} />
      ) : isFileLimitReached ? (
        <MaxFilesTitle>{t('importFile.maxFilesUploaded')}</MaxFilesTitle>
      ) : (
        <>
          <Grid container justifyContent="center" alignItems="center">
            <UploadIcon />
            <DropTitle>
              <Trans
                i18nKey={t('importFile.addOrDrag')}
                components={{ em: <HighlightedText /> }}
              />
            </DropTitle>
          </Grid>
          {showHint && (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              direction="column"
            >
              <DropHint>
                <Trans
                  i18nKey={t('importFile.extension', {
                    extension: translationExtensions,
                  })}
                  components={{ b: <DropHintBold /> }}
                />
              </DropHint>
              <DropHint>
                <Trans
                  i18nKey="importFile.size"
                  values={{ size: `${maxSize}${sizeUnit}` }}
                  components={{ b: <DropHintBold /> }}
                />
                {maxFilesNumber > 1 && (
                  <>
                    &nbsp;
                    <Trans
                      i18nKey="importFile.amount"
                      t={t}
                      values={{ amount: maxFilesNumber }}
                      components={{ b: <DropHintBold /> }}
                    />
                  </>
                )}
              </DropHint>
            </Grid>
          )}
        </>
      )}
    </DropWrapper>
  )
}

export default FileUploader
